import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircle, Error, Email } from '@mui/icons-material';
import { easyCheckAuthService } from '../../services/auth/authService';

const VerificationContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${theme.palette.primary.main}08 0%, transparent 50%),
                 radial-gradient(circle at 80% 20%, ${theme.palette.primary.main}08 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
}));

const VerificationCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6, 5),
  borderRadius: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: `0 20px 60px rgba(0, 0, 0, 0.08)`,
  position: 'relative',
  zIndex: 1,
  maxWidth: 520,
  width: '100%',
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  '& .icon': {
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    boxShadow: `0 8px 32px rgba(0, 0, 0, 0.1)`,
  },
  '& .success': {
    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
  },
  '& .error': {
    background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
  },
  '& .loading': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  }
}));

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token || !email) {
        setVerificationState('error');
        setMessage('Invalid verification link. Please check your email for the correct link.');
        return;
      }

      try {
        const result = await easyCheckAuthService.verifyEmail(email, token);
        
        if (result.success) {
          setVerificationState('success');
          setMessage('Email verified successfully! You can now login to your account.');
        } else {
          setVerificationState('error');
          setMessage(result.message || 'Email verification failed. The link may be expired or invalid.');
        }
      } catch (error) {
        setVerificationState('error');
        setMessage('An error occurred during verification. Please try again.');
      }
    };

    verifyEmail();
  }, [token, email]);

  const handleResendEmail = async () => {
    if (!email) return;
    
    try {
      setIsResending(true);
      const result = await easyCheckAuthService.resendVerificationEmail(email);
      
      if (result.success) {
        setMessage('Verification email sent! Please check your inbox.');
      } else {
        setMessage(result.message || 'Failed to resend verification email.');
      }
    } catch (error) {
      setMessage('Error sending verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const renderIcon = () => {
    switch (verificationState) {
      case 'loading':
        return (
          <div className="icon loading">
            <CircularProgress size={40} sx={{ color: 'white' }} />
          </div>
        );
      case 'success':
        return (
          <div className="icon success">
            <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
          </div>
        );
      case 'error':
        return (
          <div className="icon error">
            <Error sx={{ fontSize: 40, color: 'white' }} />
          </div>
        );
      default:
        return null;
    }
  };

  const renderTitle = () => {
    switch (verificationState) {
      case 'loading':
        return 'Verifying Email...';
      case 'success':
        return 'Email Verified!';
      case 'error':
        return 'Verification Failed';
      default:
        return '';
    }
  };

  return (
    <VerificationContainer>
      <Container component="main" maxWidth="sm">
        <VerificationCard elevation={0}>
          <IconContainer>
            {renderIcon()}
          </IconContainer>
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            {renderTitle()}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {message}
          </Typography>

          {verificationState === 'success' && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              Your account is now active. You can login with your credentials.
            </Alert>
          )}

          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} justifyContent="center">
            {verificationState === 'success' && (
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                sx={{ borderRadius: 2, px: 4 }}
              >
                Go to Login
              </Button>
            )}

            {verificationState === 'error' && email && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleResendEmail}
                disabled={isResending}
                startIcon={isResending ? <CircularProgress size={20} /> : <Email />}
                sx={{ borderRadius: 2, px: 4 }}
              >
                {isResending ? 'Sending...' : 'Resend Email'}
              </Button>
            )}

            <Button
              variant="text"
              size="large"
              onClick={() => navigate('/signup')}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Back to Signup
            </Button>
          </Stack>
        </VerificationCard>
      </Container>
    </VerificationContainer>
  );
};

export default EmailVerificationPage; 